<script setup>
import {Link, useForm} from "@inertiajs/inertia-vue3";
import Layout from "../Layouts/Authenticated.vue";
import InputText from "primevue/inputtext";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dropdown from "primevue/dropdown";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { onBeforeMount, onMounted, ref, watch } from "vue";
import axios from "axios";
import { debounce } from "../utils/debounce"
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import {useToast} from "primevue/usetoast";

const props = defineProps([
    'canManageCustomers',
    'isSuperAdmin',
    'companyOptions',
]);
const programLabelMap = ref({
    'muscle_building': 'Muskelaufbau',
    'local_fat_deposits': 'Lokale Fettpolster',
    'overweight': 'Übergewicht',
    'longevity': 'Longevity',
    'cellulite': 'Cellulite',
    'incontinence': 'Inkontinenz',
    'stress': 'Stress',
    'tiredness': 'Müdigkeit',
    'fitness_training': 'Fitness-Training',
})
const toast = useToast();
const dt = ref(null)
const customers = ref([])
const totalRecords = ref(0)
const loading = ref(false)
const search = ref('')
const selectedCompany = ref(null)
const first = ref(0)
const isVisibleDeleteDialog = ref(false)
const customerDelete = ref(null)
const deleting = ref(false)

const getParams = (extra = {}) => {
    return {
        s: search.value,
        company_id: selectedCompany.value,
        ...extra
    }
}

const debounceSearch = debounce(async () => {
    loading.value = true

    const { data } = await axios.get('/ajax/customers', {
        params: getParams()
    })

    customers.value = data.customers.data
    totalRecords.value = data.customers.total
    first.value = 0

    loading.value = false
}, 300)

const onPage = async (e) => {
    loading.value = true

    const { data } = await axios.get('/ajax/customers', {
        params: getParams({ page: e.page + 1 })
    })

    customers.value = data.customers.data

    loading.value = false
}

const showDeleteCustomerDialog = (e, customer) => {
    customerDelete.value = customer
    isVisibleDeleteDialog.value = true
}

const deleteCustomer = (e) => {
    deleting.value = true

    useForm({}).delete(`/customers/${customerDelete.value.uuid}`, {
        preserveState: false,

        onSuccess: (page) => {
            const fullName = (customerDelete.value.first_name + ' ' + customerDelete.value.last_name).trim()

            toast.add({
                severity: "success",
                summary: `${fullName} wurde gelöscht`,
                life: 5000,
            });
        },

        onFinish: (visit) => {
            isVisibleDeleteDialog.value = false
            deleting.value = false
        }
    });
}

onBeforeMount( async () => {
    loading.value = true

    const { data } = await axios.get('/ajax/customers')

    customers.value = data.customers.data
    totalRecords.value = data.customers.total

    loading.value = false
})

watch(selectedCompany, () => {
    debounceSearch()
})
</script>
<template>
    <Layout>
        <div class="lato max-w-7xl mx-auto p-4 my-10">
            <div class="px-2 text-xl font-semibold mb-7">Kunden</div>
            
            <div class="flex gap-4 mb-4 items-end px-2">
                <div class="flex-grow">
                    <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Suche</label>
                    <IconField iconPosition="right">
                        <InputIcon class="pi pi-search"></InputIcon>

                        <InputText v-model="search"
                                   class="w-full !h-[42px]"
                                   placeholder="Nachname, Name, Email"
                                   @input="debounceSearch"></InputText>
                    </IconField>
                </div>

                <div v-if="isSuperAdmin" class="w-64 shrink-0">
                    <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                    <Dropdown v-model="selectedCompany"
                              :options="companyOptions"
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Alle Unternehmen"
                              class="w-full !h-[42px] flex items-center"
                              showClear
                              filter />
                </div>
            </div>
            <div class="">
                <DataTable :value="customers"
                           :totalRecords="totalRecords"
                           :loading="loading"
                           :rows="10"
                           dataKey="id"
                           ref="dt"
                           @page="onPage($event)"
                           lazy
                           paginator>
                    <Column header="Nachname" field="last_name"></Column>

                    <Column header="Name" field="first_name"></Column>

                    <Column header="Email" field="email">
                        <template #body="props">

                            <Link :href="(canManageCustomers) ? `/customers/${props.data.uuid}/trainingsplan` : `/customer-sessions/${props.data.uuid}/start` ">{{ props.data.email }}</Link>
                        </template>
                    </Column>

                    <Column header="Program">
                        <template #body="props">
                            {{
                                (props.data.plan?.program)
                                    ? programLabelMap[props.data.plan?.program]
                                    : (
                                        (props.data.answer?.programs?.length)
                                            ? programLabelMap[props.data.answer.programs[0]]
                                            : ''
                                    )

                            }}
                        </template>
                    </Column>

                    <Column header="">
                        <template v-if="canManageCustomers"
                                  #body="props">
                            <i class="pi pi-trash cursor-pointer"
                               @click="showDeleteCustomerDialog($event, props.data)" />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <Dialog v-model:visible="isVisibleDeleteDialog"
                header="Konfirmation"
                :style="{ width: '40rem' }"
                modal>
            <div class="mb-7">
                <div v-if="customerDelete"
                     class="flex items-center gap-4">
                    <i class="pi pi-exclamation-triangle text-2xl"></i>

                    Sind Sie sicher, dass Sie {{ customerDelete.first_name }} {{ customerDelete.last_name }} löschen wollen?
                </div>
            </div>


            <div class="flex justify-end gap-2">
                <Button
                    type="button"
                    label="Abbrechen"
                    severity="secondary"
                    class="text-sm"
                    @click="isVisibleDeleteDialog = false" />

                <Button
                    :loading="deleting"
                    type="button"
                    label="Löschen"
                    class="text-sm"
                    @click="deleteCustomer"></Button>
            </div>
        </Dialog>
    </Layout>
</template>
